import '@cloudscape-design/global-styles/index.css';
import './styles/cloudscape-tokens.scss';
import './global.css';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { detectDevice, DEVICE_MOBILE } from './v2/platform/detectDevice';
import { isMobileV2PilotUser } from './v2/platform/mobileV2Allowlist';
import { CURRENT_STAGE, Stage } from './contstant/apiEndpoints';

const container = document.getElementById('root');
if (!container) {
  throw new Error('Failed to find the root element');
}
const root = createRoot(container);

// Mobile v2 app activates when:
//   1. Running on a mobile device in any non-prod stage (local dev,
//      test, beta, gamma) — prod mobile stays on the legacy v1 UI, OR
//   2. The URL contains ?v2=mobile (any environment, including prod), OR
//   3. The user's alias (from localStorage) is in the v2 mobile pilot
//      allowlist (prod only — non-prod already activates for all users)
//
// Prod-gating uses the KatalWebpackPlugin-injected stage rather than
// `NODE_ENV` so a production webpack build targeting gamma still
// activates v2 mobile automatically.
const isNonProdStage = CURRENT_STAGE !== Stage.PROD;
const hasV2Param = new URLSearchParams(window.location.search).get('v2') === 'mobile';

// Read the cached alias from localStorage (set by Midway auth on prior visits).
// On a cold first visit the key won't exist yet — the user falls through to
// legacy and picks up v2 on the next page load once the alias is stored.
const storedAlias = (() => {
  try {
    const raw = localStorage.getItem('MIDWAY_AUTH_USERNAME');
    return raw ? (JSON.parse(raw) as string) : null;
  } catch {
    return null;
  }
})();
const isPilotUser = isMobileV2PilotUser(storedAlias);

const isMobileV2 =
  hasV2Param ||
  (isNonProdStage && detectDevice() === DEVICE_MOBILE) ||
  (isPilotUser && detectDevice() === DEVICE_MOBILE);

if (isMobileV2) {
  // Initialize RUM before rendering so events from the first render are captured.
  // v2 mobile previously had no RUM init — events were silently dropped.
  const { initializeRum } = require('./rum');
  void initializeRum();

  import('./v2/Root').then(({ Root }) => {
    root.render(
      <React.StrictMode>
        <Root />
      </React.StrictMode>,
    );
  });
} else {
  const { default: App } = require('./App');
  const {
    FeatureFlagsProvider,
    getFeatureFlagsForStage,
  } = require('./context/FeatureFlagsContext');
  const { initializeRum } = require('./rum');

  void initializeRum();
  root.render(
    <React.StrictMode>
      <FeatureFlagsProvider apiFlags={getFeatureFlagsForStage()}>
        <App />
      </FeatureFlagsProvider>
    </React.StrictMode>,
  );
}
